import { QUERY_KEY_ILT_REGISTRATIONS } from '@/queries/iltRegistrations/iltRegistrationKeys.ts'
import type { GroupType, MaybeRef } from '@/types'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import {
  deleteUserGroup,
  patchUser,
  patchUserGroupsBatch,
  patchUserNote,
  postUserActiveToggle,
  postUserInReportsToggle,
  postUserProductActivityCertification,
  postUserProductCertification,
  postUserScenarioCertification,
} from './userApi'
import {
  MUTATION_KEY_USER,
  MUTATION_KEY_USER_ACTIVE,
  MUTATION_KEY_USER_GROUP_DELETE,
  MUTATION_KEY_USER_GROUPS_BATCH,
  MUTATION_KEY_USER_NOTE,
  MUTATION_KEY_USER_PRODUCT,
  MUTATION_KEY_USER_REPORT,
  MUTATION_KEY_USER_SCENARIO,
  QUERY_KEY_USER_GROUPS,
  QUERY_KEY_USER_NOTE,
  QUERY_KEY_USER_PORTAL_STATUS,
  QUERY_KEY_USER_PRODUCT_BUNDLE_PROGRESS,
  QUERY_KEY_USER_PRODUCT_PROGRESS,
  QUERY_KEY_USER_PRODUCT_STATUS,
  QUERY_KEY_USER_V1,
  QUERY_KEY_USER_V3,
} from './userKeys'

export const usePostUserProductCertification = (options?: {
  onSuccess?: () => void
  onError?: () => void
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEY_USER_PRODUCT],
    mutationFn: postUserProductCertification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ILT_REGISTRATIONS] })
      queryClient.invalidateQueries({ queryKey: [MUTATION_KEY_USER_PRODUCT] })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_PRODUCT_STATUS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_PRODUCT_PROGRESS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_PRODUCT_BUNDLE_PROGRESS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_PORTAL_STATUS],
      })
      options?.onSuccess?.()
    },
    onError: () => {
      options?.onError?.()
    },
  })
}

export const usePostUserProductActivityCertification = () => {
  const mutation = useMutation({
    mutationKey: [MUTATION_KEY_USER_PRODUCT],
    mutationFn: postUserProductActivityCertification,
  })

  return mutation
}

export const usePostUserScenarioCertification = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: [MUTATION_KEY_USER_SCENARIO],
    mutationFn: postUserScenarioCertification,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_PRODUCT_STATUS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_PRODUCT_PROGRESS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_PRODUCT_BUNDLE_PROGRESS],
      })
    },
  })

  return mutation
}

export const usePatchUser = () => {
  const mutation = useMutation({
    mutationKey: [MUTATION_KEY_USER],
    mutationFn: patchUser,
  })

  return mutation
}

export const usePostUserActiveToggle = () => {
  const mutation = useMutation({
    mutationKey: [MUTATION_KEY_USER_ACTIVE],
    mutationFn: postUserActiveToggle,
  })

  return mutation
}

export const usePostUserInReportsToggle = () => {
  const mutation = useMutation({
    mutationKey: [MUTATION_KEY_USER_REPORT],
    mutationFn: postUserInReportsToggle,
  })

  return mutation
}

export const useDeleteUserGroup = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: [MUTATION_KEY_USER_GROUP_DELETE],
    mutationFn: deleteUserGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_USER_GROUPS] })
    },
  })

  return mutation
}

export const usePatchUserGroupsBatch = (
  mutationType: MaybeRef<{
    filterGroupType: GroupType
    filterAdditional?: boolean
  }>
) => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: [MUTATION_KEY_USER_GROUPS_BATCH, mutationType],
    mutationFn: patchUserGroupsBatch,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_GROUPS, variables.userId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_V1, variables.userId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_V3, variables.userId],
      })
    },
  })

  return mutation
}

export const usePatchUserNote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEY_USER_NOTE],
    mutationFn: patchUserNote,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_NOTE, variables.userId],
      })
    },
  })
}
